/**
 * How this repository patches three's shader text: edits that each replace one anchored text, one
 * installer that writes them into `THREE.ShaderChunk` once, for every program compiled after the
 * call, and the helpers that splice a program's own text at compile.
 */
import * as THREE from 'three';
import { warn } from '../../logger.js';
/** Where the block that opens before `from` ends, or -1 when its `until` is missing after `from`. */
function blockEnd(text, from, until) {
    const at = text.indexOf(until, from);
    return at < 0 ? -1 : at + until.length;
}
/**
 * The text with the edit applied, or null when three's text is missing from it, occurs more than
 * once, or opens a block whose end is missing. A replacement by slicing, not a string `replace`: a
 * `$` in GLSL stays literal.
 */
function applyEdit(text, { three, until, godot }) {
    const at = text.indexOf(three);
    if (at < 0 || text.indexOf(three, at + three.length) >= 0)
        return null;
    const end = until === undefined ? at + three.length : blockEnd(text, at + three.length, until);
    if (end < 0)
        return null;
    return text.slice(0, at) + godot + text.slice(end);
}
/** The text with every edit applied in order, up to the first edit whose text is missing. */
function applyEditsUntilMissing(text, edits) {
    let edited = text;
    for (const edit of edits) {
        const next = applyEdit(edited, edit);
        if (next === null)
            return { text: edited, missing: edit };
        edited = next;
    }
    return { text: edited, missing: null };
}
/** The text with every edit applied in order, or null when one is missing, as `applyEdit`. */
function applyEdits(text, edits) {
    const { text: edited, missing } = applyEditsUntilMissing(text, edits);
    return missing === null ? edited : null;
}
/**
 * `text` of the shader named `shader` with every edit applied in order. It throws when an edit's
 * text is missing or occurs more than once, so a three that moved the text fails loudly, not with a
 * silently unpatched program. Every splice of a program's text goes through this, `editedProgram`
 * or `rewrittenShader`.
 */
export function editedShader(text, edits, shader) {
    const { text: edited, missing } = applyEditsUntilMissing(text, edits);
    if (missing !== null)
        throw new Error(`expected ${shader} to hold "${missing.three}" once, got:\n${edited}`);
    return edited;
}
/**
 * Applies the edits of each stage to `program`, as `editedShader`, and names the stage and `patch`
 * in the error. A stage with no list keeps its text.
 */
export function editedProgram(program, edits, patch) {
    if (edits.vertex) {
        program.vertexShader = editedShader(program.vertexShader, edits.vertex, `the vertex shader of the ${patch}`);
    }
    if (edits.fragment) {
        program.fragmentShader = editedShader(program.fragmentShader, edits.fragment, `the fragment shader of the ${patch}`);
    }
}
/**
 * `text` with every match of `pattern` rewritten, and with no match left as it is. It serves a rule
 * about a family of lines, not an anchor three must hold once, so a program without a match is no
 * error. `shaderTextSpliceConformance.test.ts` allows a `.replace` on shader text only here.
 */
export function rewrittenShader(text, pattern, rewrite) {
    return text.replace(pattern, rewrite);
}
/** three's own pattern for a chunk include (`WebGLProgram.js`). */
const INCLUDE = /^[ \t]*#include +<([\w./]+)>/gm;
/**
 * `source` with `rewrite` applied to its own text and to every chunk an include names whose text
 * the rewrite changes, nested chunks too, inlined in place of the include. A chunk the rewrite
 * leaves as it is stays an include, so a later patch still finds it. `rewrite` must leave its own
 * output as it is.
 *
 * @canonical A rewrite of every sample a program draws
 */
export function rewrittenThroughChunks(source, rewrite) {
    const inlined = rewrittenShader(source, INCLUDE, (include, name) => {
        const chunk = THREE.ShaderChunk[name];
        if (chunk === undefined)
            return include;
        const rewritten = rewrittenThroughChunks(chunk, rewrite);
        return rewritten === chunk ? include : rewritten;
    });
    return rewrite(inlined);
}
/**
 * three's chunk `name` as it stands now, the patches installed so far included, with `edit` applied,
 * as `editedShader`.
 */
export function editedChunk(name, edit) {
    return editedShader(THREE.ShaderChunk[name], [edit], `three's ${name}`);
}
/** The chunks with every edit applied in order, or null when one is missing, as `applyEdit`. */
function applyChunkEdits(chunks, edits) {
    const patched = { ...chunks };
    for (const edit of edits) {
        const chunk = applyEdit(patched[edit.chunk], edit);
        if (chunk === null)
            return null;
        patched[edit.chunk] = chunk;
    }
    return patched;
}
/** Each program with its edits applied, or null when a program or an edit's text is missing. */
function patchedPrograms(patch) {
    const { fragmentShaders = {}, vertexShaders = {} } = patch;
    const patched = [];
    for (const name of programNames(patch)) {
        const program = THREE.ShaderLib[name];
        const vertexShader = program ? applyEdits(program.vertexShader, vertexShaders[name] ?? []) : null;
        const fragmentShader = program ? applyEdits(program.fragmentShader, fragmentShaders[name] ?? []) : null;
        if (!program || vertexShader === null || fragmentShader === null)
            return null;
        patched.push({ program, vertexShader, fragmentShader });
    }
    return patched;
}
/** Each program the patch edits in either stage, once. */
function programNames({ fragmentShaders = {}, vertexShaders = {}, }) {
    return [...new Set([...Object.keys(fragmentShaders), ...Object.keys(vertexShaders)])];
}
/** The items as an English list that ends with "or". */
function alternatives(items) {
    return items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} or ${items.at(-1)}`;
}
/** The text of the chunk or program, the first of them, whose edit writes the mark. */
function markedText(patch) {
    const { edits = [], mark, fragmentShaders = {}, vertexShaders = {} } = patch;
    const chunkEdit = edits.find((edit) => edit.godot.includes(mark));
    if (chunkEdit)
        return THREE.ShaderChunk[chunkEdit.chunk];
    const stages = [
        [fragmentShaders, 'fragmentShader'],
        [vertexShaders, 'vertexShader'],
    ];
    for (const [shaders, stage] of stages)
        for (const [name, programEdits] of Object.entries(shaders))
            if (programEdits.some((edit) => edit.godot.includes(mark)))
                return THREE.ShaderLib[name][stage];
    throw new Error(`expected an edit of the ${patch.patch} patch to write its mark "${mark}", got none`);
}
/** Each chunk the patch edits, prepends to or appends to, once. */
function patchedChunkNames({ edits = [], prepend = {}, append = {}, }) {
    const names = [...edits.map((edit) => edit.chunk), ...Object.keys(prepend), ...Object.keys(append)];
    return [...new Set(names)];
}
/** The chunks with every edit applied and each added text in, or null as `applyChunkEdits`. */
function appliedEdits(chunks, { edits = [], prepend = {}, append = {} }) {
    const patched = applyChunkEdits(chunks, edits);
    if (patched === null)
        return null;
    for (const [name, text] of Object.entries(prepend))
        patched[name] = text + patched[name];
    for (const [name, text] of Object.entries(append))
        patched[name] += text;
    return patched;
}
/** The warning for a three that lacks an edit's text: each edited chunk and program, and the patch. */
function missingTextWarning(patch) {
    const edited = [...new Set((patch.edits ?? []).map((edit) => edit.chunk))];
    const stages = [
        ...Object.keys(patch.fragmentShaders ?? {}).map((name) => `${name} fragment shader`),
        ...Object.keys(patch.vertexShaders ?? {}).map((name) => `${name} vertex shader`),
    ];
    return `[Shading] three's ${alternatives([...edited, ...stages])} lacks a line the ${patch.patch} patch replaces`;
}
/**
 * Written only by `installChunkEdits`: the text each patch last failed on, by patch name. Several
 * patches install one prerequisite, so it fails once for each of them on one three.
 */
const failedOnText = new Map();
/** Every chunk and program text the patch edits, as one string to compare. */
function editedText(patch) {
    return [
        ...patchedChunkNames(patch).map((name) => THREE.ShaderChunk[name]),
        ...programNames(patch).flatMap((name) => [
            THREE.ShaderLib[name]?.vertexShader,
            THREE.ShaderLib[name]?.fragmentShader,
        ]),
    ].join('\0');
}
/**
 * Patches three's chunks and programs, and is true when they hold the patch after the call. It
 * first installs each patch in `requires`, and one that fails skips this patch. A three release
 * that lacks a replaced text keeps all of three's own text, and the call warns once and is false.
 * A second call changes nothing.
 *
 * @canonical A text edit on three's shader chunks
 */
export function installChunkEdits(patch) {
    if (markedText(patch).includes(patch.mark))
        return true;
    if (!(patch.requires ?? []).every((install) => install())) {
        warn(`[Shading] the ${patch.patch} patch is skipped, as a patch it reads did not install`);
        return false;
    }
    const text = editedText(patch);
    if (failedOnText.get(patch.patch) === text)
        return false;
    const current = Object.fromEntries(patchedChunkNames(patch).map((name) => [name, THREE.ShaderChunk[name]]));
    const patched = appliedEdits(current, patch);
    const programs = patchedPrograms(patch);
    if (patched === null || programs === null) {
        failedOnText.set(patch.patch, text);
        warn(missingTextWarning(patch));
        return false;
    }
    Object.assign(THREE.ShaderChunk, patched);
    for (const { program, vertexShader, fragmentShader } of programs) {
        program.vertexShader = vertexShader;
        program.fragmentShader = fragmentShader;
    }
    return true;
}
//# sourceMappingURL=chunkPatch.js.map